/**
 * Add / edit day itinerary item after place search (or manual entry).
 * Mirrors iOS AddItemView / EditItemView for notes + item-level attachments.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Segmented,
  Button,
  Popconfirm,
  Spin,
  message,
} from 'antd';
import { DeleteOutlined, FileAddOutlined, PictureOutlined } from '@ant-design/icons';
import { dayViewEn } from '../../content/trips.en';
import type {
  ItineraryItem,
  ItineraryItemAttachment,
  ItineraryItemLocation,
  ItineraryItemType,
  TripAttachmentRef,
} from '../../types/client';
import type { PlaceDetails } from '../../services/googlePlacesService';
import { uploadDeviceSyncFile } from '../../services/filesService';
import styles from './AddDayItemModal.module.css';

export type AddDayItemType = Exclude<ItineraryItemType, 'accommodation' | 'other' | 'note'>;

export interface AddDayItemPrefill {
  title?: string;
  type?: ItineraryItemType;
  url?: string;
  phone?: string;
  address?: string;
  time?: string;
  durationMinutes?: number;
  notes?: string;
  confirmationNumber?: string;
  /** From Place Search / existing location — not shown as editable coords. */
  latitude?: number;
  longitude?: number;
}

export interface AddDayItemSavePayload {
  item: Omit<ItineraryItem, 'id'> & { id?: string };
  attachmentRefs: TripAttachmentRef[];
}

interface PendingAttachment {
  attachment: ItineraryItemAttachment;
  fileId: string;
}

interface AddDayItemModalProps {
  open: boolean;
  mode?: 'add' | 'edit';
  /** When editing, the existing item (id preserved on save). */
  editingItem?: ItineraryItem | null;
  prefill?: AddDayItemPrefill | null;
  onClose: () => void;
  onSave: (payload: AddDayItemSavePayload) => void;
  onDelete?: () => void;
}

const TYPE_OPTIONS_ADD = [
  { label: dayViewEn.typeActivity, value: 'activity' },
  { label: dayViewEn.typeMeal, value: 'meal' },
  { label: dayViewEn.typeTransport, value: 'transport' },
];

/** Default durations — meal 90m, activity 60m (mirrors common iOS defaults). */
export function defaultDurationForType(type: ItineraryItemType | undefined): number | undefined {
  if (type === 'meal') return 90;
  if (type === 'activity') return 60;
  return undefined;
}

function typeLabel(type: ItineraryItemType | undefined): string {
  if (type === 'meal') return dayViewEn.typeMeal;
  if (type === 'transport') return dayViewEn.typeTransport;
  if (type === 'accommodation') return dayViewEn.hotel;
  if (type === 'activity') return dayViewEn.typeActivity;
  return dayViewEn.typeActivity;
}

function validCoords(lat?: number, lng?: number): { latitude: number; longitude: number } | null {
  if (
    typeof lat !== 'number' ||
    typeof lng !== 'number' ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    (lat === 0 && lng === 0)
  ) {
    return null;
  }
  return { latitude: lat, longitude: lng };
}

/** Build location from address text + optional Places coords (mirrors iOS Location). */
export function buildItemLocation(
  address: string | undefined,
  latitude?: number,
  longitude?: number
): ItineraryItemLocation | undefined {
  const trimmed = address?.trim() ?? '';
  if (!trimmed) return undefined;
  const coords = validCoords(latitude, longitude);
  return coords ? { address: trimmed, ...coords } : { address: trimmed };
}

export function prefillFromPlace(
  place: PlaceDetails,
  suggestedType: AddDayItemType
): AddDayItemPrefill {
  const coords = validCoords(place.latitude, place.longitude);
  return {
    title: place.name,
    type: suggestedType,
    url: place.website,
    phone: place.phone,
    address: place.address || undefined,
    durationMinutes: defaultDurationForType(suggestedType),
    latitude: coords?.latitude,
    longitude: coords?.longitude,
  };
}

export function prefillFromItem(item: ItineraryItem): AddDayItemPrefill {
  return {
    title: item.title,
    type: item.type,
    url: item.url,
    phone: item.phone,
    address: item.location?.address ?? item.address,
    time: item.time,
    durationMinutes: item.durationMinutes,
    notes: item.notes,
    confirmationNumber: item.confirmationNumber,
    latitude: item.location?.latitude,
    longitude: item.location?.longitude,
  };
}

function newAttachmentId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const AddDayItemModal: React.FC<AddDayItemModalProps> = ({
  open,
  mode = 'add',
  editingItem,
  prefill,
  onClose,
  onSave,
  onDelete,
}) => {
  const [form] = Form.useForm();
  /** Coords from Place Search / existing location — kept while address stays non-empty. */
  const placeCoordsRef = useRef<{ latitude: number; longitude: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const isEdit = mode === 'edit';

  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [knownRefs, setKnownRefs] = useState<TripAttachmentRef[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    placeCoordsRef.current = validCoords(prefill?.latitude, prefill?.longitude);
    const type = prefill?.type ?? 'activity';
    const durationMinutes =
      prefill?.durationMinutes ??
      (isEdit ? undefined : defaultDurationForType(type));
    form.setFieldsValue({
      type,
      title: prefill?.title ?? '',
      time: prefill?.time ?? '',
      durationMinutes,
      notes: prefill?.notes ?? '',
      confirmationNumber: prefill?.confirmationNumber ?? '',
      url: prefill?.url ?? '',
      phone: prefill?.phone ?? '',
      address: prefill?.address ?? '',
    });

    const existing = editingItem?.attachments ?? [];
    setPendingAttachments(
      existing.map((attachment) => ({
        attachment,
        fileId: '', // resolved from trip on Day View via existing refs; new uploads set fileId
      }))
    );
    setKnownRefs([]);
  }, [open, prefill, form, isEdit, editingItem]);

  const handleTypeChange = (value: string | number): void => {
    if (isEdit) return;
    const type = String(value) as ItineraryItemType;
    const current = form.getFieldValue('durationMinutes') as number | undefined | null | '';
    const nextDefault = defaultDurationForType(type);
    const isUnset = current == null || current === '';
    const isStandardDefault = current === 60 || current === 90;
    if (nextDefault != null && (isUnset || isStandardDefault)) {
      form.setFieldsValue({ durationMinutes: nextDefault });
    } else if (nextDefault == null && isStandardDefault) {
      form.setFieldsValue({ durationMinutes: undefined });
    }
  };

  const handleFiles = async (files: FileList | null, imagesOnly: boolean): Promise<void> => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const next: PendingAttachment[] = [...pendingAttachments];
      const nextRefs: TripAttachmentRef[] = [...knownRefs];
      for (const file of Array.from(files)) {
        const mime = file.type || 'application/octet-stream';
        if (imagesOnly && !mime.startsWith('image/')) {
          message.error(dayViewEn.attachmentUploadFailed);
          continue;
        }
        const fileId = await uploadDeviceSyncFile(file, mime, file.name);
        const attachmentId = newAttachmentId();
        next.push({
          attachment: {
            id: attachmentId,
            fileName: file.name,
            fileSize: file.size,
            mimeType: mime,
            createdAt: new Date().toISOString(),
          },
          fileId,
        });
        nextRefs.push({ attachmentId, fileId });
      }
      setPendingAttachments(next);
      setKnownRefs(nextRefs);
    } catch {
      message.error(dayViewEn.attachmentUploadFailed);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const removePending = (attachmentId: string): void => {
    setPendingAttachments((prev) => prev.filter((p) => p.attachment.id !== attachmentId));
    setKnownRefs((prev) => prev.filter((r) => r.attachmentId !== attachmentId));
  };

  const handleSave = async (): Promise<void> => {
    try {
      const values = await form.validateFields();
      const title = String(values.title ?? '').trim();
      if (!title) return;

      const durationRaw = values.durationMinutes;
      const durationMinutes =
        typeof durationRaw === 'number' && durationRaw >= 0
          ? durationRaw
          : durationRaw != null && durationRaw !== ''
            ? Number(durationRaw)
            : undefined;

      const address = values.address ? String(values.address).trim() || undefined : undefined;
      const coords = placeCoordsRef.current;
      const location = buildItemLocation(address, coords?.latitude, coords?.longitude);
      const type: ItineraryItemType =
        isEdit && editingItem?.type
          ? editingItem.type
          : ((values.type as ItineraryItemType) || 'activity');

      const notes = values.notes ? String(values.notes).trim() || undefined : undefined;
      const metadata = { ...(editingItem?.metadata ?? {}) };
      if (notes) metadata.notes = notes;
      else delete metadata.notes;

      const attachments = pendingAttachments.map((p) => p.attachment);
      const attachmentRefs: TripAttachmentRef[] = [
        ...knownRefs,
        ...pendingAttachments
          .filter((p) => p.fileId)
          .map((p) => ({ attachmentId: p.attachment.id, fileId: p.fileId })),
      ].filter(
        (r, idx, arr) => arr.findIndex((x) => x.attachmentId === r.attachmentId) === idx
      );

      onSave({
        item: {
          ...(isEdit && editingItem?.id ? { id: editingItem.id } : {}),
          title,
          type,
          time: values.time ? String(values.time).trim() || undefined : undefined,
          durationMinutes:
            typeof durationMinutes === 'number' && !Number.isNaN(durationMinutes)
              ? durationMinutes
              : undefined,
          notes,
          metadata,
          attachments,
          confirmationNumber: values.confirmationNumber
            ? String(values.confirmationNumber).trim() || undefined
            : undefined,
          url: values.url ? String(values.url).trim() || undefined : undefined,
          phone: values.phone ? String(values.phone).trim() || undefined : undefined,
          location,
          activityId: editingItem?.activityId,
          mealId: editingItem?.mealId,
          hotelId: editingItem?.hotelId,
          accommodationCheckInDate:
            type === 'accommodation' ? editingItem?.accommodationCheckInDate : undefined,
          accommodationCheckOutDate:
            type === 'accommodation' ? editingItem?.accommodationCheckOutDate : undefined,
        },
        attachmentRefs,
      });
    } catch {
      /* validation errors shown by form */
    }
  };

  const lockedType = isEdit ? editingItem?.type ?? prefill?.type : undefined;

  return (
    <Modal
      open={open}
      title={isEdit ? dayViewEn.editItem : dayViewEn.addItem}
      onCancel={onClose}
      destroyOnClose
      width={480}
      footer={
        <div className={styles.footer}>
          {isEdit && onDelete ? (
            <Popconfirm
              title={dayViewEn.deleteItemConfirm}
              okText={dayViewEn.deleteItem}
              okType="danger"
              cancelText={dayViewEn.cancel}
              onConfirm={onDelete}
            >
              <Button danger className={styles.deleteBtn}>
                {dayViewEn.deleteItem}
              </Button>
            </Popconfirm>
          ) : (
            <span />
          )}
          <div className={styles.footerRight}>
            <Button onClick={onClose}>{dayViewEn.cancel}</Button>
            <Button type="primary" onClick={() => void handleSave()} disabled={uploading}>
              {dayViewEn.saveItem}
            </Button>
          </div>
        </div>
      }
    >
      <Form form={form} layout="vertical" className={styles.form}>
        {isEdit ? (
          <div className={styles.typeReadonly}>
            <div className={styles.typeReadonlyLabel}>{dayViewEn.typeLabel}</div>
            <div className={styles.typeBadge}>{typeLabel(lockedType)}</div>
            <Form.Item name="type" hidden>
              <Input />
            </Form.Item>
          </div>
        ) : (
          <Form.Item name="type" label={dayViewEn.typeLabel}>
            <Segmented
              className={styles.typeSegmented}
              options={TYPE_OPTIONS_ADD}
              block
              onChange={handleTypeChange}
            />
          </Form.Item>
        )}
        <Form.Item
          name="title"
          label={dayViewEn.titleLabel}
          rules={[{ required: true, message: dayViewEn.titleRequired }]}
        >
          <Input />
        </Form.Item>
        <div className={styles.row}>
          <Form.Item name="time" label={dayViewEn.timeLabel} className={styles.half}>
            <Input placeholder="14:30" />
          </Form.Item>
          <Form.Item name="durationMinutes" label={dayViewEn.durationLabel} className={styles.half}>
            <InputNumber min={0} className={styles.fullWidth} />
          </Form.Item>
        </div>
        <Form.Item name="address" label={dayViewEn.address}>
          <Input />
        </Form.Item>
        <Form.Item name="phone" label={dayViewEn.phone}>
          <Input />
        </Form.Item>
        <Form.Item name="url" label={dayViewEn.website}>
          <Input />
        </Form.Item>
        <Form.Item name="confirmationNumber" label={dayViewEn.confirmationLabel}>
          <Input />
        </Form.Item>
        <Form.Item name="notes" label={dayViewEn.notesLabel}>
          <Input.TextArea rows={3} />
        </Form.Item>

        <div className={styles.attachmentsSection} data-testid="item-attachments-section">
          <div className={styles.attachmentsLabel}>{dayViewEn.attachmentsLabel}</div>
          {pendingAttachments.length === 0 ? (
            <p className={styles.attachmentsEmpty}>{dayViewEn.noAttachments}</p>
          ) : (
            <ul className={styles.attachmentsList}>
              {pendingAttachments.map((p) => (
                <li key={p.attachment.id} className={styles.attachmentRow}>
                  <span className={styles.attachmentName}>{p.attachment.fileName}</span>
                  <button
                    type="button"
                    className={styles.attachmentRemove}
                    aria-label={dayViewEn.removeAttachment}
                    onClick={() => removePending(p.attachment.id)}
                  >
                    <DeleteOutlined />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className={styles.attachmentActions}>
            <button
              type="button"
              className={styles.attachmentIconBtn}
              aria-label={dayViewEn.addAttachmentFile}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <FileAddOutlined />
            </button>
            <button
              type="button"
              className={styles.attachmentIconBtn}
              aria-label={dayViewEn.addAttachmentPhoto}
              disabled={uploading}
              onClick={() => photoInputRef.current?.click()}
            >
              <PictureOutlined />
            </button>
            {uploading ? <Spin size="small" /> : null}
            <input
              ref={fileInputRef}
              type="file"
              className={styles.hiddenInput}
              data-testid="item-attachment-file-input"
              onChange={(e) => void handleFiles(e.target.files, false)}
            />
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              className={styles.hiddenInput}
              data-testid="item-attachment-photo-input"
              onChange={(e) => void handleFiles(e.target.files, true)}
            />
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default AddDayItemModal;
